import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { apiGet } from '../lib/api';
import type { QueueStateDto } from '@bisa/shared';

export function useQueueRealtime(facilityId: string) {
  const [queues, setQueues] = useState<QueueStateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchQueues = useCallback(async () => {
    try {
      const data = await apiGet<QueueStateDto[]>(`/facilities/${facilityId}/queues/today`);
      setQueues(data);
      setError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  // Debounced refetch so rapid realtime events don't thrash the API
  const debouncedFetch = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchQueues();
    }, 200);
  }, [fetchQueues]);

  useEffect(() => {
    fetchQueues();

    const channel = supabase
      .channel(`queue-realtime:${facilityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'queue_entries',
          filter: `facility_id=eq.${facilityId}`,
        },
        debouncedFetch,
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'queues',
          filter: `facility_id=eq.${facilityId}`,
        },
        debouncedFetch,
      )
      .subscribe();

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      supabase.removeChannel(channel);
    };
  }, [facilityId, fetchQueues, debouncedFetch]);

  return { queues, loading, error, refetch: fetchQueues };
}
